import React, { useEffect, useState, useRef } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Bar, Polar, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  DatePicker,
  Table,
  Tag,
  Avatar,
  Modal,
  Upload,
  Button,
  Popconfirm,
  Spin,
  Popover
} from 'antd';
import { faCheckCircle, faClipboardCheck, faEye, faFileExcel, faPencil, faPlus, faTimesCircle, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'antd/es/typography/Link';
import { userAtom, notificationsAtom, userPrivilagesAtom } from '../../../../atoms/Atoms';
import { H1, H4 } from '../../../shared-components/typography/Title';
import api from '../../../../api/api';
import {
  toCurrency,
  thousand,
  getDatePresets,
  randomColor,
  getLibertyReportColumns,
  cleanLibertyDataForExport,
  getLibertyExportFields
} from '../../../../functions';
import SelectMultiple from '../../../shared-components/form/SelectMultiple';
import Select from '../../../shared-components/form/Select';
import CustomButton from '../../../shared-components/buttons/Button';
import Form from '../../../shared-components/form/Form';
import DateSelector from '../../../shared-components/form/DatePicker';
import Input from '../../../shared-components/form/Input';
import envConfig from '../../../../envConfig';

function OnHand() {
  const { t, i18n } = useTranslation();
  const chartRef = useRef();
  const { RangePicker } = DatePicker;
  const user = useRecoilValue(userAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dateRange, setDateRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearch, setLastSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedPartNumbers, setSelectedPartNumbers] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedProcessAreas, setSelectedProcessAreas] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [serializedFilter, setSerializedFilter] = useState(0);

  function getData(selectedPage, sTerm) {
    // if (!dateRange.start || !dateRange.end) {
    //   return;
    // }
    setLoading(true);
    setLastSearch(sTerm);
    setCurrentPage(selectedPage);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.add(-1 * utcOffset, 'minute').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/inventory/liberty/graph', {
        StartDate: startString,
        EndDate: endString,
        Query: sTerm,
        Page: selectedPage,
        Size: pageSize,
        Categories: selectedCategories,
        Locations: selectedLocations,
        ProcessAreas: selectedProcessAreas,
        Manufacturers: selectedManufacturers,
        PartNumbers: selectedPartNumbers,
        SerializedFilter: serializedFilter
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReportData(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function exportData() {
    // if (!dateRange.start || !dateRange.end) {
    //   return;
    // }
    setLoading(true);
    // setLastSearch(sTerm);
    // setCurrentPage(selectedPage);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.add(-1 * utcOffset, 'minute').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/inventory/liberty/excel', {
        StartDate: startString,
        EndDate: endString,
        Query: lastSearch,
        ForExport: true,
        Categories: selectedCategories,
        Locations: selectedLocations,
        ProcessAreas: selectedProcessAreas,
        Manufacturers: selectedManufacturers,
        PartNumbers: selectedPartNumbers,
        SerializedFilter: serializedFilter
      }, {
        responseType: 'blob'
      })
      .then((response) => {
        setLoading(false);
        const fileName = `${t('on_hand_inventory_report')}.xlsx`;
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function isRangeMoreThanMonth() {
    try {
      const startDate = dayjs(dateRange?.start);
      const endDate = dayjs(dateRange?.end);
      const monthDiff = endDate.diff(startDate, 'month', true);
      return monthDiff > 1;
    } catch (err) {
      return false;
    }
  }

  useEffect(() => {
    getData(0, lastSearch);
  }, [pageSize]); 

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').add(-1, 'year').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
    setSelectedCategories([]);
    setSelectedLocations([]);
    setSelectedManufacturers([]);
    setSelectedPartNumbers([]);
  }, []);

  useEffect(() => {
    getData(0, lastSearch);
  }, [dateRange, serializedFilter]); // add filters sorts here

  return (
    <div className="space-y-2">
      <div className="w-full grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
        <SelectMultiple
          className="col-span-1 lg:col-span-2 xl:col-span-1 xl:col-start-2"
          disabled={loading}
          label={t('locations')}
          id="locations"
          name="locations"
          value={selectedLocations}
          onChange={(obj) => {
            setSelectedLocations(obj);
          }}
          options={_.map(_.filter(reportData?.locations, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('process_areas')}
          id="processareas"
          name="processareas"
          value={selectedProcessAreas}
          onChange={(obj) => {
            setSelectedProcessAreas(obj);
          }}
          options={_.map(_.filter(reportData?.processAreas, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        {/* <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('categories')}
          id="categories"
          name="categories"
          value={selectedCategories}
          onChange={(obj) => {
            setSelectedCategories(obj);
          }}
          options={_.map(_.filter(reportData?.categories, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        /> */}
        <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('manufacturers')}
          id="manufacturers"
          name="manufacturers"
          value={selectedManufacturers}
          onChange={(obj) => {
            setSelectedManufacturers(obj);
          }}
          options={_.map(_.filter(reportData?.manufacturers, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <SelectMultiple
          className="col-span-1 md:col-span-2 lg:col-span-2 lg:col-span-2 xl:col-span-1"
          disabled={loading}
          label={t('part_number')}
          id="partnumbers"
          name="partnumbers"
          value={selectedPartNumbers}
          onChange={(obj) => {
            setSelectedPartNumbers(obj);
          }}
          options={_.map(_.filter(reportData?.partNumbers, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <Select
          className="col-span-1 lg:col-span-2 xl:col-span-1 mt-6"
          disabled={loading}
          label={t('serialized_filter')}
          id="serializedfilter"
          name="serializedfilter"
          value={serializedFilter}
          onChange={(obj) => setSerializedFilter(parseInt(obj.target.value, 10))}
          options={[
            {
              key: '0',
              value: '0',
              text: t('both')
            },
            {
              key: '1',
              value: '1',
              text: t('serialized')
            },
            {
              key: '2',
              value: '2',
              text: t('non_serialized')
            },
          ]}
        />
        
        {/* <div className="col-span-1 lg:col-start-3 lg:col-span-2 xl:col-span-1 xl:col-start-auto">
          <RangePicker
            disabled={loading}
            className="w-full h-11 mt-2 md:mt-6 bg-transparent"
            disabledDate={(current) => current && current.valueOf() > Date.now()}
            presets={getDatePresets(t)}
            value={[
              dayjs(dateRange?.start),
              dayjs(dateRange?.end)
            ]}
            onChange={(obj) => {
              setDateRange({
                start: obj[0],
                end: obj[1]
              });
            }}
            format="MMM DD, YYYY"
          />
        </div> */}
      </div>
      <div className="flex space-x-2">
        <Input
          label={t('search')}
          type="text"
          name="search"
          key="search"
          value={searchTerm}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              getData(0, searchTerm)
            }
          }}
          onChange={(obj) => setSearchTerm(obj.target.value)}
        />
        <CustomButton
          className="bg-primaryColor px-4"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          loading={loading}
          text={t('search')}
          onClick={() => getData(0, searchTerm)}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="col-span-1" style={{ paddingBottom: 30 }}>
          <Spin spinning={loading}>
            <div className="text-center" style={{ height: 500 }}>
              <p className="font-bold">{t('manufacturer_distribution')}</p>
              <Pie
                ref={chartRef}
                data={{
                  labels: _.map(reportData?.manufacturerDistribution, (s) => s.label),
                  datasets: [
                    {
                      label: t('count'),
                      backgroundColor: _.map(reportData?.manufacturerDistribution, (i) => `${randomColor()}cc`),
                      borderWidth: 1,
                      data: _.map(reportData?.manufacturerDistribution, (x) => x.count)
                    }
                  ]
                }}
                options={{
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: false,
                    },
                    datalabels: {
                      // anchor: 'end',
                      // offset: 8,
                      // rotation: 330,
                      color: 'black',
                      formatter: (value, context) => thousand(value),
                      labels: {
                        title: {
                          font: {
                            weight: 'regular'
                          }
                        }
                      }
                    }
                  },
                }}
              />
            </div>
          </Spin>
        </div>
        <div className="col-span-1" style={{ paddingBottom: 30 }}>
          <Spin spinning={loading}>
            <div className="text-center" style={{ height: 500 }}>
              <p className="font-bold">{t('process_area_distribution')}</p>
              <Pie
                ref={chartRef}
                data={{
                  labels: _.map(reportData?.processAreaDistribution, (s) => s.label),
                  datasets: [
                    {
                      label: t('count'),
                      backgroundColor: _.map(reportData?.processAreaDistribution, (i) => `${randomColor()}cc`),
                      borderWidth: 1,
                      data: _.map(reportData?.processAreaDistribution, (x) => x.count)
                    }
                  ]
                }}
                options={{
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: false,
                    },
                    datalabels: {
                      // anchor: 'end',
                      // offset: 8,
                      // rotation: 330,
                      color: 'black',
                      formatter: (value, context) => thousand(value),
                      labels: {
                        title: {
                          font: {
                            weight: 'regular'
                          }
                        }
                      }
                    }
                  },
                }}
              />
            </div>
          </Spin>
        </div>
        <Table
          className="col-span-1 md:col-span-2"
          bordered
          locale={{
            emptyText: t('no_data')
          }}
          size="small"
          title={() => (
            <div className="flex">
              <div className="flex items-center space-x-2 ml-auto">
                {isRangeMoreThanMonth() && (
                  <Popover trigger="hover" content={t('max_of_one_month_for_export')}>
                    <div>
                      <CustomButton
                        className="bg-green-600 px-4"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="bg-green-600"
                        loading={loading}
                        disabled
                        icon={faFileExcel}
                        text={t('export')}
                        onClick={() => exportData()}
                      />
                    </div>
                  </Popover>
                )}
                {!isRangeMoreThanMonth() && (
                  <CustomButton
                    className="bg-green-600 px-4"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="bg-green-600"
                    loading={loading}
                    icon={faFileExcel}
                    text={t('export')}
                    onClick={() => exportData()}
                  />
                )}
                <strong>{t('last_updated_on')}:</strong>
                {reportData?.lastUpdatedAt && (
                  <span>{dayjs(reportData?.lastUpdatedAt).add(-4, 'hour').format('MMM DD, YYYY hh:mm A')}</span>
                )}
              </div>
            </div>
          )}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize,
            showSizeChanger: true,
            current: currentPage + 1,
            total: reportData?.total,
            onChange: (page, size) => {
              if (page - 1 !== currentPage) {
                setCurrentPage(page - 1);
                getData(page - 1, lastSearch);
              } else {
                setPageSize(size);
              }
            }
          }}
          dataSource={reportData?.inventory}
          columns={getLibertyReportColumns(t)}
        />
      </div>
    </div>
  );
}

export default OnHand;
